import React, { type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Modal from '@mui/joy/Modal'
import { ModalDialog, Typography } from '@mui/joy'
import InfoIcon from '@mui/icons-material/Info'

import { FONT_WEIGHT } from './typography'

export function ReservationConfirmationModal({
    popupText,
    open,
    handleClose,
    handleConfirm,
    loading,
    errorMessage,
}: {
    popupText: string,
    open: boolean,
    handleClose: () => void,
    handleConfirm: () => void | Promise<void>,
    loading: boolean,
    errorMessage: string | null,
}) {
    const { t } = useTranslation()
    const stopPropagation = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
        >
            <ModalDialog
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                sx={{
                maxWidth: '400px',
                padding: '20px 14px 14px',
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2rem',
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: '1rem',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}>
                        <InfoIcon sx={{
                            color: '#FFCC00',
                            fontSize: '26px',
                        }}/>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '.75rem', // 12px',
                        }}>
                            <Typography
                                sx={{
                                    color: '#0B0B0B',
                                    fontSize: '15px',
                                    lineHeight: '20px',
                                    fontWeight: FONT_WEIGHT.SEMIBOLD,
                                }}
                                component={'h2'}
                            >
                                {t('garage.confirmationModal.maxDimensionsTitle')}
                            </Typography>
                            <Box
                                fontSize={15 }
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: backend passes this as HTML
                                dangerouslySetInnerHTML={{
                                    __html: popupText,
                                }}
                            />
                            {errorMessage &&
                                <Typography
                                    level="body-sm"
                                    sx={{
                                        color: 'danger.500',
                                        textAlign: 'center',
                                    }}
                                >
                                    {errorMessage}
                                </Typography>
                            }
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        gap: '13px',
                        justifyContent: 'right',
                    }} >
                        <Button
                            variant="plain"
                            color="neutral"
                            sx={{
                                fontSize: '15px',
                                lineHeight: '20px',
                                borderRadius: '7px',
                                fontWeight: FONT_WEIGHT.SEMIBOLD,
                            }}
                            onClick={event => {
                                stopPropagation(event)
                                handleClose()
                            }}
                        >
                            {t('garage.confirmationModal.cancel')}
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            sx={{
                                fontSize: '15px',
                                lineHeight: '20px',
                                borderRadius: '7px',
                                fontWeight: FONT_WEIGHT.SEMIBOLD,
                            }}
                            onClick={async event => {
                                stopPropagation(event)
                                await handleConfirm()
                            }}
                            loading={loading}
                        >
                            {t('garage.confirmationModal.complete')}
                        </Button>
                    </Box>
                </Box>
            </ModalDialog>
        </Modal>
    )
}
